/**
 * 동기화 복구 시스템
 * - 브라우저 재시작 후 미완료 동기화 감지
 * - 중단된 지점부터 동기화 재시작
 * - 동기화 상태 세션 간 유지
 */

class SyncRecovery {
  constructor() {
    this.storageKey = 'FDES_SYNC_STATE';
    this.recoveryTimeout = 30000; // 30초 후 복구 시작
  }

  /**
   * 동기화 시작 시 상태 저장
   */
  saveSyncState(syncProgress) {
    const syncState = {
      isActive: true,
      startedAt: new Date().toISOString(),
      totalReports: syncProgress.total,
      completedReports: syncProgress.completed,
      failedReports: syncProgress.failed,
      lastUpdateAt: new Date().toISOString(),
      sessionId: this.generateSessionId()
    };

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(syncState));
      // console.log('[SyncRecovery] 동기화 상태 저장:', syncState);
    } catch (error) {
      // console.error('[SyncRecovery] 동기화 상태 저장 실패:', error);
    }
  }

  /**
   * 동기화 진행 상황 업데이트
   */
  updateSyncState(syncProgress) {
    try {
      const existingState = this.getSyncState();
      if (existingState && existingState.isActive) {
        const updatedState = {
          ...existingState,
          completedReports: syncProgress.completed,
          failedReports: syncProgress.failed,
          lastUpdateAt: new Date().toISOString()
        };
        localStorage.setItem(this.storageKey, JSON.stringify(updatedState));
        // console.log('[SyncRecovery] 동기화 상태 업데이트:', updatedState);
      }
    } catch (error) {
      // console.error('[SyncRecovery] 동기화 상태 업데이트 실패:', error);
    }
  }

  /**
   * 동기화 완료 시 상태 정리
   */
  clearSyncState() {
    try {
      localStorage.removeItem(this.storageKey);
      // console.log('[SyncRecovery] 동기화 상태 정리 완료');
    } catch (error) {
      // console.error('[SyncRecovery] 동기화 상태 정리 실패:', error);
    }
  }

  /**
   * 저장된 동기화 상태 조회
   */
  getSyncState() {
    try {
      const savedState = localStorage.getItem(this.storageKey);
      if (savedState) {
        return JSON.parse(savedState);
      }
      return null;
    } catch (error) {
      // console.error('[SyncRecovery] 동기화 상태 조회 실패:', error);
      return null;
    }
  }

  /**
   * 페이지 로드 시 복구 필요 여부 확인
   */
  needsRecovery() {
    const syncState = this.getSyncState();

    if (!syncState || !syncState.isActive) {
      return false;
    }

    // 마지막 업데이트로부터 일정 시간이 지났으면 복구 필요
    const lastUpdate = new Date(syncState.lastUpdateAt);
    const timeDiff = Date.now() - lastUpdate.getTime();

    if (timeDiff > this.recoveryTimeout) {
      // console.log('[SyncRecovery] 복구 필요 감지:', {
      //   timeSinceLastUpdate: `${Math.round(timeDiff / 1000)}초`,
      //   syncState
      // });
      return true;
    }

    return false;
  }

  /**
   * 복구 정보 반환
   */
  getRecoveryInfo() {
    const syncState = this.getSyncState();
    if (!syncState) {
      return null;
    }

    const startedAt = new Date(syncState.startedAt);
    const timeSinceStart = Date.now() - startedAt.getTime();

    return {
      ...syncState,
      timeSinceStart: Math.round(timeSinceStart / 1000),
      formattedStartTime: startedAt.toLocaleString()
    };
  }

  /**
   * 복구 시작 알림
   */
  notifyRecoveryStart(recoveryInfo, callback) {
    // console.log('[SyncRecovery] 동기화 복구 시작:', recoveryInfo);

    // 사용자에게 복구 시작 알림
    if (callback) {
      callback('recovery_started', recoveryInfo);
    }

    // 복구 시작 시점 기록
    const updatedState = {
      ...recoveryInfo,
      recoveryStartedAt: new Date().toISOString(),
      isRecovering: true
    };

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(updatedState));
    } catch (error) {
      // console.error('[SyncRecovery] 복구 상태 저장 실패:', error);
    }
  }

  /**
   * 세션 ID 생성 (중복 실행 방지용)
   */
  generateSessionId() {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * 오래된 복구 상태 정리 (1일 이상)
   */
  cleanupOldStates() {
    const syncState = this.getSyncState();
    if (!syncState) return;

    const startedAt = new Date(syncState.startedAt);
    const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);

    if (startedAt.getTime() < oneDayAgo) {
      // console.log('[SyncRecovery] 오래된 동기화 상태 정리');
      this.clearSyncState();
    }
  }

  /**
   * 복구 시스템 초기화
   */
  initialize(backgroundSyncInstance) {
    // console.log('[SyncRecovery] 복구 시스템 초기화');

    // 오래된 상태 정리
    this.cleanupOldStates();

    // 복구 필요 여부 확인
    if (this.needsRecovery()) {
      const recoveryInfo = this.getRecoveryInfo();
      // console.log('[SyncRecovery] 미완료 동기화 발견, 복구 준비 중...');

      // 인증 토큰이 설정될 때까지 대기 후 복구 시작
      setTimeout(() => {
        if (backgroundSyncInstance.authToken) {
          this.startRecovery(backgroundSyncInstance, recoveryInfo);
        } else {
          // console.log('[SyncRecovery] 인증 토큰 없음, 토큰 설정 후 복구 예정');
          // backgroundSync에서 토큰 설정 시 복구하도록 플래그 설정
          window.FDES_RECOVERY_PENDING = recoveryInfo;
        }
      }, 2000); // 2초 후 복구 시도
    }
  }

  /**
   * 실제 복구 실행
   */
  async startRecovery(backgroundSyncInstance, recoveryInfo) {
    try {
      this.notifyRecoveryStart(recoveryInfo, backgroundSyncInstance.notifyListeners.bind(backgroundSyncInstance));

      // 기존 동기화가 진행 중이 아니라면 새로 시작
      if (!backgroundSyncInstance.isRunning) {
        // console.log('[SyncRecovery] 동기화 복구 시작');
        await backgroundSyncInstance.checkAndStartSync();
      } else {
        // console.log('[SyncRecovery] 동기화가 이미 진행 중, 복구 불필요');
        this.clearSyncState();
      }
    } catch (error) {
      // console.error('[SyncRecovery] 복구 실행 실패:', error);
    }
  }
}

// 싱글톤 인스턴스
const syncRecovery = new SyncRecovery();

export const saveSyncState = (syncProgress) => syncRecovery.saveSyncState(syncProgress);
export const updateSyncState = (syncProgress) => syncRecovery.updateSyncState(syncProgress);
export const clearSyncState = () => syncRecovery.clearSyncState();
export const needsRecovery = () => syncRecovery.needsRecovery();
export const getRecoveryInfo = () => syncRecovery.getRecoveryInfo();
export const initializeRecovery = (backgroundSyncInstance) => syncRecovery.initialize(backgroundSyncInstance);
export const startRecovery = (backgroundSyncInstance, recoveryInfo) => syncRecovery.startRecovery(backgroundSyncInstance, recoveryInfo);

export default syncRecovery;